<template>
  <div class="flame-preloader" :class="{ 'fade-out': fadeOut, 'compact': compact }">
    <!-- Animated flame logo -->
    <div class="flame-container">
      <!-- Logo container -->
      <div class="logo-wrapper" :style="compact ? { width: size + 'px', height: size + 'px' } : {}">
        <!-- Animated background glow effects - behind the logo -->
        <div class="glow-container">
          <div class="glow glow-1" />
        </div>
        <div class="glow-container">
          <div class="glow glow-2" />
        </div>
        <div class="glow-container">
          <div class="glow glow-3" />
        </div>

        <!-- SVG Logo - circle static, flame animated -->
        <svg class="logo-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 39.9943 39.9943" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="paint0_linear_1_150" x1="19.9984" x2="19.9984" y1="0.531539" y2="39.4652" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stop-color="#D54C26" />
              <stop offset="100%" stop-color="#772006" />
            </linearGradient>
            <clipPath id="clip0_1_150">
              <rect fill="white" height="39.9943" width="39.9943" />
            </clipPath>
            
            <!-- Turbulence filter for realistic flame effect -->
            <filter id="flame-turbulence" x="-50%" y="-50%" width="200%" height="200%">
              <feTurbulence 
                type="fractalNoise" 
                :baseFrequency="`${baseFreqX} ${baseFreqY}`" 
                :numOctaves="numOctaves" 
                result="turbulence"
                seed="10"
              >
                <!-- Animate the turbulence seed for flickering effect -->
                <animate
                  attributeName="seed"
                  values="10;15;8;12;18;10"
                  :dur="`${turbulenceDuration}s`"
                  repeatCount="indefinite"
                />
                <!-- Animate base frequency for dynamic movement -->
                <animate
                  attributeName="baseFrequency"
                  values="0.02 0.08; 0.025 0.09; 0.018 0.075; 0.022 0.085; 0.02 0.08"
                  dur="4s"
                  repeatCount="indefinite"
                />
              </feTurbulence>
              <feDisplacementMap 
                in="SourceGraphic" 
                in2="turbulence" 
                :scale="displacementScale" 
                xChannelSelector="R" 
                yChannelSelector="G"
              >
                <!-- Animate displacement intensity -->
                <animate
                  attributeName="scale"
                  values="3;4.5;2.5;4;3"
                  :dur="`${displacementDuration}s`"
                  repeatCount="indefinite"
                />
              </feDisplacementMap>
            </filter>
          </defs>
          
          <g clipPath="url(#clip0_1_150)">
            <!-- Static background circle - no animations -->
            <path :d="svgPaths.p3b594970" fill="#410700" />
            <path clip-rule="evenodd" :d="svgPaths.p2a049700" fill="url(#paint0_linear_1_150)" fill-rule="evenodd" />
            
            <!-- Animated flame group with turbulence filter -->
            <g filter="url(#flame-turbulence)" style="transform-origin: 50% 70%">
              <g class="animate-flame-scale" style="transform-origin: 50% 70%">
                <path :d="svgPaths.p383cfe00" fill="#FF9E30" />
                <path :d="svgPaths.p14a9f300" fill="#F34C43" />
                <path :d="svgPaths.p22d1e000" fill="#F2FFFD" />
                <path :d="svgPaths.p36493300" fill="#FFEBB0" />
                <path :d="svgPaths.p1ed93380" fill="#FCB865" />
                <path :d="svgPaths.p32c58500" fill="#FFBD7A" />
                <path :d="svgPaths.p35de8100" fill="#FEF1F1" />
                <path :d="svgPaths.p13e83f00" fill="#FFF1E0" />
                <path :d="svgPaths.pb5aad00" fill="#FF7259" />
                <path :d="svgPaths.p1d92c500" fill="#FDA3A2" />
                <path :d="svgPaths.pdc45d00" fill="#FF8E7A" />
                <path :d="svgPaths.p1e9b7600" fill="#FCC27C" />
                <path :d="svgPaths.p2e65a4c0" fill="#FEF8E7" />
                <path :d="svgPaths.p4f09c80" fill="#FFEBBF" />
                <path :d="svgPaths.pbe05e80" fill="#FCD17B" />
                <path :d="svgPaths.p18136900" fill="#FCB777" />
                <path :d="svgPaths.p1a79d400" fill="#FF6E5E" />
                <path :d="svgPaths.p18c80580" fill="#FC8165" />
                <path :d="svgPaths.p35aab100" fill="#F2FFFD" />
                <path :d="svgPaths.p35719180" fill="#EBFDFF" />
                <path :d="svgPaths.p869f00" fill="#F34C43" />
                <path :d="svgPaths.p3195eb70" fill="#FCDF7C" />
                <path :d="svgPaths.pe2a0b00" fill="#CC1A00" />
                <path :d="svgPaths.pdb9d80" fill="#E86248" />
                <path :d="svgPaths.p2022e80" fill="#FEF6F0" />
                <path :d="svgPaths.p291b4000" fill="#D9443C" />
                <path :d="svgPaths.pabdce80" fill="#D32B1B" />
                <path :d="svgPaths.p54c1200" fill="#EBFDFF" />
                <path :d="svgPaths.p37bf7080" fill="#FFE47A" />
                <path :d="svgPaths.p1fdfcbc0" fill="#FDC2C1" />
                <path :d="svgPaths.p2bed7fc0" fill="#CC1A00" />
                <path :d="svgPaths.p7f8cc00" fill="#FF9E7A" />
                <path :d="svgPaths.p15171a00" fill="#FAE4D5" />
                <path :d="svgPaths.p2a225e00" fill="#FFB657" />
                <path :d="svgPaths.p12768d00" fill="#CC1A00" />
                <path :d="svgPaths.p2bc6ce00" fill="#D1452A" />
                <path :d="svgPaths.p1bfda300" fill="#FCD977" />
                <path :d="svgPaths.p1f25ea00" fill="#FCD4A1" />
                <path :d="svgPaths.p27e71400" fill="#FDE9C1" />
                <path :d="svgPaths.p20a61240" fill="#F34C43" />
                <path :d="svgPaths.p19e87600" fill="#D32B1B" />
                <path :d="svgPaths.p29a5100" fill="#FEDED5" />
              </g>
            </g>
          </g>
        </svg>
      </div>

      <!-- Loading text -->
      <div v-if="!compact" class="loading-text-container">
        <div class="loading-text">
          Loading...
        </div>
        
        <!-- Progress bar -->
        <div v-if="showProgress" class="progress-bar-container">
          <div 
            class="progress-bar"
            :style="{ width: `${progress}%` }"
          />
        </div>
      </div>
    </div>

    <!-- Animation Controls Panel -->
    <div v-if="showControls" class="controls-panel">
      <!-- Header -->
      <div 
        class="controls-header"
        @click="controlsOpen = !controlsOpen"
      >
        <span class="controls-title">Flame Controls</span>
        <span class="controls-toggle">
          {{ controlsOpen ? '▼' : '▶' }}
        </span>
      </div>

      <!-- Controls -->
      <div v-if="controlsOpen" class="controls-body">
        <!-- Base Frequency X -->
        <div class="control-item">
          <div class="control-label-row">
            <label class="control-label">Base Frequency X</label>
            <span class="control-value">{{ baseFreqX.toFixed(3) }}</span>
          </div>
          <input
            v-model.number="baseFreqX"
            type="range"
            min="0.001"
            max="0.1"
            step="0.001"
            class="control-slider"
          />
        </div>

        <!-- Base Frequency Y -->
        <div class="control-item">
          <div class="control-label-row">
            <label class="control-label">Base Frequency Y</label>
            <span class="control-value">{{ baseFreqY.toFixed(3) }}</span>
          </div>
          <input
            v-model.number="baseFreqY"
            type="range"
            min="0.001"
            max="0.2"
            step="0.001"
            class="control-slider"
          />
        </div>

        <!-- Displacement Scale -->
        <div class="control-item">
          <div class="control-label-row">
            <label class="control-label">Displacement Scale</label>
            <span class="control-value">{{ displacementScale.toFixed(1) }}</span>
          </div>
          <input
            v-model.number="displacementScale"
            type="range"
            min="0"
            max="20"
            step="0.5"
            class="control-slider"
          />
        </div>

        <!-- Num Octaves -->
        <div class="control-item">
          <div class="control-label-row">
            <label class="control-label">Turbulence Octaves</label>
            <span class="control-value">{{ numOctaves }}</span>
          </div>
          <input
            v-model.number="numOctaves"
            type="range"
            min="1"
            max="8"
            step="1"
            class="control-slider"
          />
        </div>

        <!-- Turbulence Duration -->
        <div class="control-item">
          <div class="control-label-row">
            <label class="control-label">Turbulence Duration (s)</label>
            <span class="control-value">{{ turbulenceDuration.toFixed(1) }}</span>
          </div>
          <input
            v-model.number="turbulenceDuration"
            type="range"
            min="0.5"
            max="10"
            step="0.5"
            class="control-slider"
          />
        </div>

        <!-- Displacement Duration -->
        <div class="control-item">
          <div class="control-label-row">
            <label class="control-label">Displacement Duration (s)</label>
            <span class="control-value">{{ displacementDuration.toFixed(1) }}</span>
          </div>
          <input
            v-model.number="displacementDuration"
            type="range"
            min="0.5"
            max="10"
            step="0.5"
            class="control-slider"
          />
        </div>

        <!-- Reset Button -->
        <button
          @click="resetControls"
          class="reset-button"
        >
          Reset to Defaults
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FlamePreloader',
  props: {
    onComplete: {
      type: Function,
      default: null,
    },
    duration: {
      type: Number,
      default: 3000, // in milliseconds
    },
    showProgress: {
      type: Boolean,
      default: true,
    },
    showControls: {
      type: Boolean,
      default: false,
    },
    fullscreen: {
      type: Boolean,
      default: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    size: {
      type: Number,
      default: 100, // size in pixels for compact mode
    },
    minDisplayTime: {
      type: Number,
      default: 800, // minimum time to show preloader in ms (for compact mode)
    },
  },
  data() {
    return {
      progress: 0,
      fadeOut: false,
      // Animation control states - optimized for fluid motion
      baseFreqX: 0.01,
      baseFreqY: 0.04,
      displacementScale: 2,
      numOctaves: 2,
      turbulenceDuration: 5,
      displacementDuration: 4,
      controlsOpen: true,
      progressInterval: null,
      // SVG paths data
      svgPaths: {
        p12768d00: "M30.7963 12.7888L26.147 14.4636L25.2024 20.2584L30.7963 12.7888Z",
        p13e83f00: "M3.33621 26.7567L6.50494 14.7651L9.23822 15.2742L3.33621 26.7567Z",
        p14a9f300: "M21.7456 17.4649L25.2024 20.2584L26.147 14.4636L21.7456 17.4649Z",
        p15171a00: "M19.1196 8.96356L21.3705 8.77598L22.9783 7.40264L19.1196 8.96356Z",
        p18136900: "M10.7321 23.4673L11.3887 16.0178L9.23822 15.2742L7.02748 25.5575L10.7321 23.4673Z",
        p18c80580: "M20.4929 27.8018L25.457 25.3164L25.1957 20.2585L20.4929 27.8018Z",
        p19e87600: "M25.4637 25.3163L25.2024 29.7244L29.3627 26.1336L30.1599 21.4911L25.4637 25.3163Z",
        p1a79d400: "M9.23822 15.2742L14.0683 9.9952L11.3887 16.0245L9.23822 15.2742Z",
        p1bfda300: "M7.02748 25.5575L8.5214 33.898L15.1402 26.281L7.02748 25.5575Z",
        p1d92c500: "M27.929 10.7254L28.3511 7.20165H26.0599L24.7201 10.3637L27.929 10.7254Z",
        p1e9b7600: "M15.1402 26.281L21.7524 17.4648L20.4929 27.8017L16.9155 29.9924L15.1402 26.281Z",
        p1ed93380: "M8.5214 33.898L15.1402 26.281L16.9222 29.9856L8.5214 33.898Z",
        p1f25ea00: "M16.9222 36.6246L24.834 34.4339L20.4929 27.8017L16.9222 36.6246Z",
        p1fdfcbc0: "M31.6806 20.2584L30.1599 19.3205L25.2024 20.2584L29.3627 22.1409L31.6806 20.2584Z",
        p2022e80: "M28.3511 7.20166H26.0599L33.2817 1.84229L28.3511 7.20166Z",
        p20a61240: "M27.929 10.7255L30.7963 12.7888L26.147 14.4636L27.929 10.7255Z",
        p22d1e000: "M35.2312 13.0166V8.97023L38.1587 7.79787L35.2312 13.0166Z",
        p27e71400: "M10.7321 23.4674L7.02748 25.5575L15.1402 26.281L10.7321 23.4674Z",
        p291b4000: "M30.5618 7.77778L28.3511 7.20165L27.929 10.7254L30.7963 12.7888L30.5618 7.77778Z",
        p29a5100: "M16.9222 11.2279L11.3887 16.0245L15.6159 14.8723L16.9222 11.2279Z",
        p2a049700: "M19.9971 1.06076C9.53888 1.06076 1.06077 9.53887 1.06077 19.9971C1.06077 30.4554 9.53888 38.9335 19.9971 38.9335C30.4554 38.9335 38.9335 30.4554 38.9335 19.9971C38.9335 9.53887 30.4554 1.06076 19.9971 1.06076ZM-0.00231147 19.9971C-0.00231147 8.95174 8.95175 -0.00232086 19.9971 -0.00232086C31.0425 -0.00232086 39.9966 8.95174 39.9966 19.9971C39.9966 31.0425 31.0425 39.9966 19.9971 39.9966C8.95175 39.9966 -0.00231147 31.0425 -0.00231147 19.9971Z",
        p2a225e00: "M12.6816 35.2512L16.9222 36.6246L20.4929 27.8017L16.9222 29.9924L12.6816 35.2512Z",
        p2bc6ce00: "M15.6159 14.8723L21.7456 17.4649L19.4947 13.0836L15.6159 14.8723Z",
        p2bed7fc0: "M33.2817 23.4674L33.6435 14.7651L31.6806 20.2585L33.2817 23.4674Z",
        p2e65a4c0: "M15.1402 26.281L21.7523 17.4648L10.7388 23.4673L15.1402 26.281Z",
        p3195eb70: "M20.4929 27.8017L16.9222 29.9924L8.5214 33.898L16.9222 36.6246L20.4929 27.8017Z",
        p32c58500: "M21.7457 17.4649L10.7321 23.4674L15.6159 14.8723L21.7457 17.4649Z",
        p35719180: "M30.1599 19.3206L31.6806 20.2585L33.6435 14.7651L30.1599 19.3206Z",
        p35aab100: "M18.8851 5.71448L21.5313 2.9343L20.5934 5.62739L18.8851 5.71448Z",
        p35de8100: "M6.50493 14.765L14.0683 9.9952L9.23821 15.2742L6.50493 14.765Z",
        p36493300: "M21.7457 17.4648L25.2024 20.2584L20.4929 27.8017L21.7457 17.4648Z",
        p37bf7080: "M28.6592 30.3608L25.2024 29.7244L24.834 34.4339L28.6592 30.3608Z",
        p383cfe00: "M20.4929 27.8017L24.834 34.434L25.1957 29.7244L25.457 25.3163L20.4929 27.8017Z",
        p3b594970: "M19.9971 39.465C30.749 39.465 39.4651 30.749 39.4651 19.9971C39.4651 9.24531 30.749 0.529222 19.9971 0.529222C9.24532 0.529222 0.529231 9.24531 0.529231 19.9971C0.529231 30.749 9.24532 39.465 19.9971 39.465Z",
        p4f09c80: "M3.33621 26.7567L7.02748 25.5575L8.52141 33.898L3.33621 26.7567Z",
        p54c1200: "M24.7201 10.3637L27.929 10.7254L26.147 14.4636L21.7456 17.4649L24.7201 10.3637Z",
        p7f8cc00: "M30.1599 21.4911L25.2024 20.2585L25.4637 25.3164L30.1599 21.4911Z",
        p869f00: "M33.2817 23.4674L31.6806 20.2585L30.1599 21.4911L29.3627 26.1337L33.2817 23.4674Z",
        pabdce80: "M28.3511 7.20166L30.5618 7.77779L33.2817 1.84229L28.3511 7.20166Z",
        pb5aad00: "M16.9222 11.2279L19.1196 8.96357L19.4947 13.0836L15.6159 14.8723L16.9222 11.2279Z",
        pbe05e80: "M3.33621 26.7566L9.23822 15.2742L7.02748 25.5575L3.33621 26.7566Z",
        pdb9d80: "M11.3887 16.0245L15.6159 14.8723L10.7321 23.4674L11.3887 16.0245Z",
        pdc45d00: "M24.7201 10.3637L19.4947 13.0836L21.7457 17.4649L24.7201 10.3637Z",
        pe2a0b00: "M19.1196 8.9636L21.3705 8.77602L19.4947 13.0836L19.1196 8.9636Z",
      },
    }
  },
  mounted() {
    if (!this.compact) {
      this.startProgress()
    }
  },
  beforeDestroy() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
    }
  },
  methods: {
    startProgress() {
      this.progressInterval = setInterval(() => {
        if (this.progress >= 100) {
          clearInterval(this.progressInterval)
          // Trigger fade out
          this.fadeOut = true
          // Wait for fade animation to complete before calling onComplete
          if (this.onComplete) {
            setTimeout(this.onComplete, 500)
          }
          return
        }
        this.progress += 100 / (this.duration / 50)
      }, 50)
    },
    resetControls() {
      this.baseFreqX = 0.01
      this.baseFreqY = 0.04
      this.displacementScale = 2
      this.numOctaves = 2
      this.turbulenceDuration = 5
      this.displacementDuration = 4
    },
  },
}
</script>

<style scoped>
.flame-preloader {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #181729;
  opacity: 1;
  transition: opacity 0.5s ease-out;
}

.flame-preloader.compact {
  position: relative;
  width: auto;
  height: auto;
  background: transparent;
  inset: auto;
}

.flame-preloader.fade-out {
  opacity: 0;
}

.flame-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

.logo-wrapper {
  position: relative;
  width: 8rem;
  height: 8rem;
}

.glow-container {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.glow {
  border-radius: 50%;
}

.glow-1 {
  width: 10rem;
  height: 10rem;
  background: rgba(249, 115, 22, 0.3);
  filter: blur(3rem);
  animation: flame-glow-1 3s ease-in-out infinite;
}

.glow-2 {
  width: 9rem;
  height: 9rem;
  background: rgba(239, 68, 68, 0.2);
  filter: blur(2rem);
  animation: flame-glow-2 3s ease-in-out infinite;
}

.glow-3 {
  width: 8rem;
  height: 8rem;
  background: rgba(234, 179, 8, 0.15);
  filter: blur(1rem);
  animation: flame-glow-3 3s ease-in-out infinite;
}

.logo-svg {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
}

.animate-flame-scale {
  animation: flame-scale 2s ease-in-out infinite;
}

.loading-text-container {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.loading-text {
  color: #fed7aa;
  animation: pulse-slow 2s ease-in-out infinite;
  letter-spacing: 0.05em;
}

.progress-bar-container {
  width: 16rem;
  height: 0.25rem;
  background: #3f3f46;
  border-radius: 9999px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(to right, #f97316, #ef4444, #eab308);
  transition: width 300ms ease-out;
  background-size: 200% 100%;
  animation: flame-shimmer 2s linear infinite;
}

/* Animation Controls Panel */
.controls-panel {
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: rgba(39, 39, 42, 0.95);
  backdrop-filter: blur(8px);
  border: 1px solid #3f3f46;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.controls-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #3f3f46;
  cursor: pointer;
}

.controls-title {
  color: #fed7aa;
  font-weight: 500;
}

.controls-toggle {
  color: #a1a1aa;
  font-size: 0.75rem;
}

.controls-body {
  padding: 1rem;
  width: 20rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.control-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.control-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.control-label {
  color: #d4d4d8;
  font-size: 0.875rem;
}

.control-value {
  color: #fb923c;
  font-size: 0.75rem;
  font-family: monospace;
}

.control-slider {
  width: 100%;
  height: 0.5rem;
  background: #3f3f46;
  border-radius: 0.5rem;
  appearance: none;
  cursor: pointer;
}

.control-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 1rem;
  height: 1rem;
  background: #ea580c;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
}

.control-slider::-webkit-slider-thumb:hover {
  background: #f97316;
  transform: scale(1.2);
}

.control-slider::-moz-range-thumb {
  width: 1rem;
  height: 1rem;
  background: #ea580c;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
}

.control-slider::-moz-range-thumb:hover {
  background: #f97316;
  transform: scale(1.2);
}

.reset-button {
  width: 100%;
  padding: 0.5rem 1rem;
  background: #ea580c;
  color: white;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 0.875rem;
}

.reset-button:hover {
  background: #c2410c;
}

/* Keyframe Animations */
@keyframes flame-scale {
  0%, 100% {
    transform: scaleY(1);
  }
  25% {
    transform: scaleY(1.05);
  }
  50% {
    transform: scaleY(0.96);
  }
  75% {
    transform: scaleY(1.02);
  }
}

@keyframes flame-glow-1 {
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  33% {
    transform: scale(1.2);
    opacity: 0.6;
  }
  66% {
    transform: scale(0.9);
    opacity: 0.9;
  }
}

@keyframes flame-glow-2 {
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  33% {
    transform: scale(1.2);
    opacity: 0.6;
  }
  66% {
    transform: scale(0.9);
    opacity: 0.9;
  }
}

@keyframes flame-glow-3 {
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  33% {
    transform: scale(1.2);
    opacity: 0.6;
  }
  66% {
    transform: scale(0.9);
    opacity: 0.9;
  }
}

@keyframes flame-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes pulse-slow {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}
</style>

